import type { JobRun } from "@/lib/jobs";

import { tallyEntries } from "./schedule";

/**
 * A run's numbers as tiles — JOBS.md's `This run` card, built at last (日本-229).
 *
 * `JobOutcome.tally` was persisted, served and typed and read by nothing, so the audio job has
 * been reporting `No source found` — a count you would want to see rising — to nobody since it
 * shipped. Every surface showed `run.detail` instead, a string each job assembled for itself.
 *
 * Lives here rather than in either screen because both want it: JOBS.md specifies the card for the
 * phone (`6j3`/`6j4`) and the desktop expanded row is where Alex actually looks. `Tile` comes with
 * it for the same reason — it was local to `JobDetail`, and `JobsPanel` does not import from there.
 */
export function TallyCard({ run }: { run: JobRun | null }) {
  const entries = tallyEntries(run);
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-[7px]">
      {entries.map(({ label, value, suffix }) => (
        <Tile
          key={label}
          value={`${value.toLocaleString()}${suffix ? ` ${suffix}` : ""}`}
          label={label}
          // Deliberately one tone for every tile. The count that matters differs per job — `Needs
          // review` for fix-formatting, `No source found` for audio — and a renderer that guessed
          // which is bad news would be wrong for the next job written.
          tone="text-ink"
        />
      ))}
    </div>
  );
}

/**
 * Value over label, mono numerals — the `8,759 fixed` / `17 errors` pair from `6j4`.
 *
 * `value` is a pre-formatted STRING, not a number: a tally key may declare a unit (`Snapshot size
 * (MB)`) that belongs beside the value, and a numeric prop cannot carry it.
 */
export function Tile({ value, label, tone }: { value: string; label: string; tone: string }) {
  return (
    // `bg-sunken`, not `bg-panel`: on the phone these sit on a tinted card and read as tiles
    // either way, but in `JobsPanel`'s expanded row the panel is already `surface` and a
    // same-colour tile is four numbers floating in whitespace. Recessed reads on both.
    //
    // No `flex-1`. Stretching four tiles across a 1147px panel spaces them like a dashboard and
    // loses that they are one breakdown; sized to content, they wrap as a group.
    <span className="flex min-w-[86px] flex-col gap-[3px] rounded-[var(--r-3)] bg-sunken px-[11px] py-2.5">
      <span className={`font-mono text-[19px]/[1] font-semibold ${tone}`}>{value}</span>
      <span className="text-meta text-ink-3">{label}</span>
    </span>
  );
}
